import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import { useMutation } from '@tanstack/react-query';
import FilesList from 'components/v2/file-row/FilesList';
import FileUpload from 'components/v2/file-upload/FileUpload';
import { HelpBox } from 'components/v2/help-boxes';
import { ButtonUploadIcon } from 'components/v2/icons';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { EPD_LCA_INDICATORS, EPD_LCA_SELF_CHECK_GUIDE_URL, EPD_LCA_TEMPLATES_URL } from 'constants/constants';
import { ChangeEvent, useEffect, useState } from 'react';
import { createFilter } from 'react-select';
import { EPDModel, FileLoadingModel, LanguageModel, UploadDocumentTypesModel } from 'services/EpdClient';
import LanguagesService from 'services/LanguagesService';
import UploadDocumentTypesService from 'services/UploadDocumentTypesService';
import styled from 'styled-components';
import { A, HyperLink } from 'styles/Styles.styled';
import { Option } from 'types/types';
import EditDialog from './EditDialog';
import EPDService from 'services/EpdService';
import isAxiosErrorWithLcaXLValidationErrors from 'util/isAxiosErrorWithLcaXLValidationErrors';
import WizardLcaResultsErrors from 'components/wizard/WizardLcaResultsErrors';
import { toaster } from 'components/v2/toast';
import { LcaXLValidationErrorCode } from 'types/lca-results/LcaXLValidationErrorCode';
import { LCAResultsOutdatedTemplateDialog } from 'components/v2/dialogs/LCAResultsOutdatedTemplateDialog';

type OptionWithCode = Option & { code?: string };

type TProps = {
  epd: EPDModel;
  isOpened: boolean;
  header: string;
  onHide: any;
  onChangeEpd: any;
  onUploadFile(field: string, file: File, documentTypeId: string | null, documentLanguageId: string | null): void;
  onRemoveFile(fileId: string): void;
  isConstruction?: boolean;
  fileLoading?: FileLoadingModel[];
  refreshEPD(epdId: string): Promise<void>;
};

const UploadDocumentsDialog = ({
  isOpened,
  onHide,
  epd,
  onChangeEpd,
  onUploadFile,
  onRemoveFile,
  isConstruction,
  fileLoading,
  refreshEPD,
}: TProps) => {
  const [languagesLookup, setLanguagesLookup] = useState<OptionWithCode[]>([]);
  const [documentTypesLookup, setDocumentTypesLookup] = useState<Option[]>([]);
  const [documentType, setDocumentType] = useState<Option>();
  const [documentLanguage, setDocumentLanguage] = useState<OptionWithCode>();
  const uploadLcaFileMutation = useMutation({
    mutationFn: (file: File) => EPDService.addLCAFile(epd.id!, file),
  });

  useEffect(() => {
    const fetchLanguages = async () => {
      const result = await LanguagesService.getLanguages();
      setLanguagesLookup(
        result?.map((x: LanguageModel) => {
          return { value: x.id, label: x.name, code: x.code };
        })
      );
    };

    const fetchDocumentTypes = async () => {
      const result = await UploadDocumentTypesService.getDocumentTypes(epd.isCompilerFormat);
      setDocumentTypesLookup(
        result?.map((x: UploadDocumentTypesModel) => {
          return { value: x.id, label: x.name };
        })
      );
    };

    fetchLanguages();
    fetchDocumentTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadLcaFile = async (e: ChangeEvent<HTMLInputElement>, propertyName: string) => {
    const target = e.target as HTMLInputElement;
    const file = target && target.files && target.files[0];
    if (!file) {
      return null;
    }

    try {
      await uploadLcaFileMutation.mutateAsync(file);
      await refreshEPD(epd.id!);
    } catch (e: any) {
      if (e?.response?.data[0]?.code === LcaXLValidationErrorCode.OUTDATED_TEMPLATE) {
        LCAResultsOutdatedTemplateDialog();
        return;
      }
      if (e?.response?.data[0]?.code === LcaXLValidationErrorCode.INVALID_VALUE_MAX_LENGTH) {
        toaster({
          severity: 'error',
          summary: 'Character limit exceeded in the uploaded file.',
          details: 'Please review the instructions, update the file, and try again.',
        });
        return;
      }
      if (isAxiosErrorWithLcaXLValidationErrors(e)) {
        return;
      }
      console.error('Failed to upload LCA results file', e);
      toaster({
        severity: 'error',
        summary: 'Something went wrong while processing your file.',
        details: 'Please try again or contact support if the issue persists.',
      });
    }
  };

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>, propertyName: string) => {
    const target = e.target as HTMLInputElement;
    let file = target && target.files && target.files[0];

    if (!file || !epd) {
      return null;
    }

    if (documentType?.value !== '2489c33d-56f6-4fe7-b5cd-a94e82900708') {
      propertyName = 'DocumentOther';
    }

    const documentTypeId = documentType?.value ?? null;
    const documentLanguageId = documentLanguage?.value ?? null;

    onUploadFile(propertyName, file, documentTypeId, documentLanguageId);
    // @ts-ignore
    setDocumentType(null);
    // @ts-ignore
    setDocumentLanguage(null);
  };

  const isLCALoading = uploadLcaFileMutation.isPending;

  const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: 'start' as const,
  };

  return (
    <EditDialog
      header="Upload documents"
      isOpened={isOpened}
      onHide={() => {
        setDocumentLanguage(undefined);
        setDocumentType(undefined);
        onHide();
      }}
    >
      <HelpBox>
        Several EPD documents could be uploaded in a PDF format.
        <br />
        Please note, the EPD document is a mandatory type.
        <br />
        All uploaded documents will be auto saved.
      </HelpBox>

      <FieldPanel>
        <WizardSelectControlledComponent
          label="Document type"
          tooltip=""
          name="docType"
          options={documentTypesLookup}
          required
          value={documentType}
          onChanged={(name: string, value: string) =>
            setDocumentType(
              value ? { value: value, label: documentTypesLookup.find((x) => x.value == value)?.label || value } : undefined
            )
          }
          isClearable={true}
        />
        <WizardSelectControlledComponent
          label="Document language"
          tooltip=""
          name="docLang"
          options={languagesLookup}
          required
          value={documentLanguage}
          onChanged={(name: string, value: string) =>
            setDocumentLanguage({
              value: value,
              label: languagesLookup.find((x) => x.value == value)?.label || value,
              code: languagesLookup.find((x) => x.value == value)?.code,
            })
          }
          isClearable={false}
          filterOption={createFilter(filterConfig)}
        />
      </FieldPanel>

      <FileUpload
        label="Upload document"
        name="Document"
        icon={ButtonUploadIcon}
        accept=".pdf"
        validExtensions={['pdf']}
        onFileUpload={uploadFile}
        disabled={!(documentLanguage && documentType)}
      />
      <FilesList
        files={epd?.documents}
        onRemoveFile={onRemoveFile}
        fileLoading={
          fileLoading?.find(
            (loading) =>
              (loading.fileType === 'Document' || loading.fileType === 'DocumentOther') && loading.loadingsCount > 0
          ) !== undefined
        }
      />

      <div style={{ marginTop: '1.75rem' }}>
        <SmartLabel label="LCA results" required={isConstruction} hasValue={(epd?.lcAs?.length || 0) > 0} />

        <HelpBox>
          <div>
            <div>Guidelines for uploading LCA results:</div>
            <UnorderedList>
              <li>
                Download the Excel template to fill in your LCA results. Ensure that the{' '}
                <strong>latest version of the "Excel template for PCR 2019:14"</strong> from{' '}
                <A href={EPD_LCA_TEMPLATES_URL} target="_blank" rel="noopener noreferrer">
                  environdec.com
                </A>{' '}
                is used for construction products, as older versions are not accepted.
              </li>
              <li>
                Refer to the latest{' '}
                <HyperLink href={EPD_LCA_INDICATORS} target="_blank" rel="noopener noreferrer">
                  characterisation factors on our website
                </HyperLink>{' '}
                for accurate data entry.
              </li>
              <li>
                Use{' '}
                <HyperLink href={EPD_LCA_SELF_CHECK_GUIDE_URL} target="_blank" rel="noopener noreferrer">
                  the self-check guide
                </HyperLink>{' '}
                to troubleshoot and correct issues if your LCA results file is declined.
              </li>
            </UnorderedList>
          </div>
        </HelpBox>
      </div>

      <FileUpload
        label={'Upload LCA results'}
        icon={ButtonUploadIcon}
        accept=".xlsx"
        validExtensions={['xlsx']}
        name={'LCA'}
        onFileUpload={uploadLcaFile}
        disabled={isLCALoading || (epd?.lcAs?.length || 0) > 0}
      />
      <FilesList files={epd?.lcAs} onRemoveFile={onRemoveFile} fileLoading={isLCALoading} />

      {isAxiosErrorWithLcaXLValidationErrors(uploadLcaFileMutation.error) && (
        <WizardLcaResultsErrors
          errors={uploadLcaFileMutation.error.response!.data}
          style={{ marginTop: 16, borderRadius: 4 }}
        />
      )}
    </EditDialog>
  );
};

const FieldPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 30px;
  justify-content: start;
  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`;

const UnorderedList = styled.ul`
  margin: 0;
  line-height: 20px;
  margin-top: 0.5em;
  padding-left: 1.75em;
`;

export default UploadDocumentsDialog;
